import React, { useEffect, useRef, useState } from "react";
import { Box, Chip, Skeleton, Typography, alpha } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FileThumbnails from "../Estimates/FileThumbnails";
import { fetchMuestraFilesCached } from "../../utils/muestraFilesRequest";
import {
  getCachedArchivos,
  hasCachedArchivos,
} from "../../utils/cacheArchivos";
import { filterMuestraCategoryFiles } from "../../utils/muestraFileCategories";

const getCategoryEstadoChipSx = (estado, theme) => {
  const colorMap = {
    Pedido: "#1565c0",
    Pendiente: "#ef6c00",
    Recibido: "#2e7d32",
  };
  const color = colorMap[estado] || theme.palette.grey[700];
  return {
    height: 20,
    fontWeight: 700,
    fontSize: "0.62rem",
    bgcolor: alpha(color, 0.12),
    color,
    border: "1px solid",
    borderColor: alpha(color, 0.28),
    flexShrink: 0,
  };
};

/**
 * Photos + name + status chip (Calidades | Print) for the Samples grid.
 */
export default function MuestraCategoryFilesCell({
  muestra,
  category,
  nombre,
  estado,
  refreshKey,
}) {
  const theme = useTheme();
  const [files, setFiles] = useState(() =>
    hasCachedArchivos(muestra?.id)
      ? getCategoryCached(muestra.id, category)
      : null,
  );
  const rootRef = useRef(null);
  const muestraId = muestra?.id;
  const nameLabel = nombre?.trim() || "";

  useEffect(() => {
    if (!muestraId) {
      setFiles([]);
      return;
    }

    if (hasCachedArchivos(muestraId)) {
      setFiles(getCategoryCached(muestraId, category));
      return;
    }

    const node = rootRef.current;
    if (!node) return;

    let cancelled = false;

    const load = async () => {
      const data = await fetchMuestraFilesCached(muestraId);
      if (!cancelled) {
        setFiles(filterMuestraCategoryFiles(data, category).slice(0, 3));
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        load();
      },
      { root: null, rootMargin: "120px", threshold: 0 },
    );

    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [muestraId, category, refreshKey]);

  return (
    <Box
      ref={rootRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        minWidth: 0,
        width: "100%",
        height: "100%",
        py: 0.25,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 0.35,
          flexShrink: 0,
          minHeight: 36,
          alignItems: "center",
        }}
      >
        {files === null && (
          <Skeleton
            variant="rectangular"
            width={36}
            height={36}
            sx={{ borderRadius: 1 }}
          />
        )}
        {files &&
          files.map((file, i) => (
            <Box
              key={`${file.path || file.name}-${i}`}
              sx={{
                width: 36,
                height: 36,
                overflow: "hidden",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                "& img": { width: "100%", height: "100%", objectFit: "cover" },
                "& > div": { width: "100%", height: "100%", padding: 0 },
              }}
            >
              <FileThumbnails filePath={file.path} />
            </Box>
          ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Typography
          variant="body2"
          noWrap
          title={nameLabel || "—"}
          sx={{ minWidth: 0, fontSize: "0.72rem", fontWeight: 600 }}
        >
          {nameLabel || "—"}
        </Typography>
        {estado ? (
          <Chip
            size="small"
            label={estado}
            sx={getCategoryEstadoChipSx(estado, theme)}
          />
        ) : null}
      </Box>
    </Box>
  );
}

function getCategoryCached(id, category) {
  const cached = getCachedArchivos(id);
  return Array.isArray(cached)
    ? filterMuestraCategoryFiles(cached, category).slice(0, 3)
    : [];
}
